import { Box, Checkbox, Divider, FormControl, Grid, TextField, Typography } from "@material-ui/core";
import { daysBetweenDates } from "common/Utils/dateCompare";
import { getFilteredSearchedData } from "common/Utils/filterSearchedData";
import { getVisibleOrders, groupeBySku } from "common/Utils/orders";
import { FbaFee, InventoryFBA, InventoryReport, Order } from "common/types";
import { TranslationPrefixes } from "consts/translationPrefixes";
import useToggleSelect from "hooks/useToggleSelect";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { getDataDB, getFees, getInventoryListingData, getInventoryUnsupressedData, getOrders, setInventoryReorderDates } from "services/service";
import { getColumns, getDynamicColumnsForExport, } from "../../common/Utils/ActionUtils";
import { TableComponentToPrint } from "../ComponentToPrint/TableComponent";
import CardComponent from "../shared/CardComponent";
import { Column } from "../shared/TableComponent";
import { TableInventory } from "./TableInventory";
import { CardHeaderActions } from "components/shared/CardHeaderActions";

const getCsvData = (data: any[]) => {
    return data.map(el => {
        Object.keys(el).forEach(key => {
            if (typeof el?.key === "string") {
                el[key] = el[key].split("<br/>").join('\n');
            }
        });
        return el;
    })
}

export interface InventoryD {
    sku: string;
    availableUnits: number;
    daysSupply: number;
    avgDailySales: number;
    reorderStatus: string;
    reorderDate: string;
    reorderUnits: number;
    inboundUnits: number;
    unfulfillableUnits: number;
    reservedUnits: number;
}

export interface ColumnIventoryOrder extends Column {
    accessor?: keyof InventoryD;
}

function Inventory() {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [currentSearch, setCurrentSearch] = useState<string>("");
    const [orders, setOrders] = useState<Order[]>([]);
    const [fees, setFees] = useState<FbaFee[]>([]);
    const [inventoryFBA, setInventoryFBA] = useState<InventoryFBA[]>([]);
    const [inventoryListingData, setInventoryListingData] = useState<InventoryReport[]>([]);
    const [uploadedData, setUploadedData] = useState<string[][]>([]);
    const [keyForForceUpdate, setKeyForForceUpdate] = useState<number>(0); // Key for force update
    const [inventoryDates, setInventoryDates] = useState<InventoryReorderDate | null>(null);
    const { sideBar, actions, inventory: inventoryPrefix } = TranslationPrefixes;

    interface InventoryReorderDate {
        [sku: string]: {
            reorderDate: string;
        }
    }
    useEffect(() => {
        setIsLoading(true);

        const fetchData = async () => {
            try {
                const [ordersDB, feesDB, inventoryDB, inventoryListing, inventoryReorderDates] = await Promise.all([
                    getOrders(),
                    getFees(),
                    getInventoryUnsupressedData(),
                    getInventoryListingData(),
                    getDataDB<InventoryReorderDate>("inventory"),
                ]);

                setOrders(ordersDB);
                setFees(feesDB);
                setInventoryFBA(inventoryDB);
                setInventoryListingData(inventoryListing);
                setInventoryDates(inventoryReorderDates);
                setIsLoading(false);
            } catch (error) {
                console.error(error);
                setIsLoading(false); // Ensure loading state is reset in case of an error
            }
        }

        fetchData();

        if (uploadedData?.length > 0) {
            uploadedData.map((item) => {
                setInventoryReorderDates(item[0], "reorderDate", item[5]);
            });
            toast.success(uploadedData.length + " items uploaded successfully");
        }
    }, [keyForForceUpdate]);

    const { t } = useTranslation();

    const calculatedOrders = getVisibleOrders(orders, fees);

    const separatedOrders = groupeBySku(calculatedOrders);

    const tableData = separatedOrders.map(sameSKUorders => {

        const inventoryFBAitem = inventoryFBA.find(el => el.sku === sameSKUorders[0].sku);
        const inventoryListingItem = inventoryListingData.find(el => el.sku === sameSKUorders[0].sku);
        const fbaItem = fees.find(el => el.sku === sameSKUorders[0].sku);
        // Reduce to calculate total quantity and total revenue

        const last90days = 90;
        const last7days = 7;
        const combineAllCalculations = sameSKUorders.reduce((acc, order) => {
            const timeDiff = daysBetweenDates(order.purchaseDate);
            const daysOfSupply = timeDiff <= last90days ? order.quantity : 0;
            const availableUnits = inventoryListingItem?.quantity ?? 0;
            const avgDailySales = timeDiff <= last7days ? order.quantity : 0;

            acc.sku = order.sku;
            acc.availableUnits = availableUnits;
            acc.avgDailySales += avgDailySales;
            acc.reorderStatus = availableUnits < 10 ? t(`${inventoryPrefix}loading`) : t(`${inventoryPrefix}аvailability`);
            acc.daysSupply += Math.ceil(daysOfSupply / 90);
            acc.reorderDate = (inventoryDates && inventoryDates[order.sku]?.reorderDate) ?? "";
            acc.reorderUnits = 0;
            acc.inboundUnits = inventoryFBAitem ? inventoryFBAitem["afn-inbound-receiving-quantity"] : 0;
            acc.unfulfillableUnits = inventoryFBAitem ? inventoryFBAitem["afn-unsellable-quantity"] : 0;
            acc.reservedUnits = 0;
            return acc;
        }, {
            sku: "",
            availableUnits: 0,
            avgDailySales: 0,
            reorderStatus: "",
            daysSupply: 0,
            reorderDate: "",
            reorderUnits: 0,
            inboundUnits: 0,
            unfulfillableUnits: 0,
            reservedUnits: 0,
        } as InventoryD);

        return combineAllCalculations;
    });


    const { allSelectedIds, toggleSelect } = useToggleSelect({ identificatorSelectors: tableData.map(el => el.sku) })

    const createTableCell = (accessor: keyof InventoryD) => {
        return {
            accessor,
            cell: (val: any) => <Typography variant="body1">{val}</Typography>,
            header: t(`${inventoryPrefix}${accessor}`)
        }
    }

    /*
SKU 
Available - UNITS
Days of Supply - UNITS
Inbound - UNITS
Unfulfillable - UNITS
Reserved – UNITS


    */

    const tableCells: (keyof InventoryD)[] = [
        "sku",
        "availableUnits",
        "daysSupply",
        "inboundUnits",
        "unfulfillableUnits",
        "reservedUnits"
    ];

    const columns: Array<ColumnIventoryOrder> = [
        {
            cell: (el) => {
                return (
                    <Checkbox
                        checked={allSelectedIds.includes(el.sku)}
                        onChange={() => toggleSelect({ singleSelector: el.sku })}
                    />
                )
            },
            header: "checkBox"
        },
        ...tableCells.map(createTableCell)
    ];

    let componentRef = useRef<TableComponentToPrint | null>(null);
    const csvColumns = getDynamicColumnsForExport({ t, prefix: TranslationPrefixes.inventory, values: Object.values(tableCells) });

    const renderHeaderActions = () => {
        if (!allSelectedIds.length) return;
        const selectedData = tableData.filter((el) => allSelectedIds.includes(el.sku));
        const csvData = getCsvData(selectedData);
        const csvFileName = `cogs_${+new Date()}.csv`;
        return (
            <CardHeaderActions
                csvHeaders={getColumns(csvColumns)}
                csvFileName={csvFileName}
                csvData={csvData}
                componentRef={componentRef}

            />
        );
    }

    return (
        <CardComponent title={t(`${sideBar}inventory`)} isLoading={isLoading} headerActions={renderHeaderActions()}>
            <Box>
                <Grid container style={{ flexGrow: 1 }} spacing={2} alignItems="center">
                    <Grid item xs={6}>
                        <FormControl style={{ width: "100%" }}>
                            <TextField
                                fullWidth
                                onChange={(e) => {
                                    setCurrentSearch(e.target.value as string);
                                }}
                                label={t(`${actions}search`)}
                            />
                        </FormControl>
                    </Grid>
                </Grid>
                <Box marginBottom="15px" marginTop="15px">
                    <Divider />
                </Box>
                {
                    tableData.length > 0 && (
                        <div style={{ display: "none" }}>
                            <TableComponentToPrint
                                ref={componentRef}
                                data={tableData}
                                headers={csvColumns}
                                title={t(`${sideBar}inventory`)}
                            />
                        </div>
                    )
                }
                <TableInventory
                    data={getFilteredSearchedData({ tableData, currentSearch }) as InventoryD[]}
                    columns={columns}
                    checkBox={{
                        checked: tableData.length === allSelectedIds.length,
                        onChangeCheckBox: (toggleAll) => {
                            toggleSelect({ toggle: toggleAll })
                        }
                    }}
                />
            </Box>
        </CardComponent>
    )
}

export default Inventory;
